'use client';

import React, { Component, type ErrorInfo, type ReactNode } from 'react';

type AppRootErrorBoundaryProps = {
  children: ReactNode;
};

type AppRootErrorBoundaryState = {
  hasError: boolean;
  errorName: string;
  errorMessage: string;
  occurredAt: string;
  componentStack: string;
};

let hasInstalledEdgeDomGuard = false;

function isEdgeBrowser() {
  if (typeof navigator === 'undefined') return false;
  return /Edg\//.test(navigator.userAgent);
}

function installEdgeDomGuard() {
  if (hasInstalledEdgeDomGuard) return;
  if (!isEdgeBrowser()) return;
  if (typeof Node === 'undefined') return;

  const originalInsertBefore = Node.prototype.insertBefore;
  const originalRemoveChild = Node.prototype.removeChild;

  (Node.prototype.insertBefore as unknown as (
    this: Node,
    newNode: Node,
    referenceNode: Node | null,
  ) => Node) = function insertBeforeGuard(
    newNode: Node,
    referenceNode: Node | null,
  ) {
    try {
      return originalInsertBefore.call(this, newNode, referenceNode);
    } catch (error) {
      if (
        error instanceof DOMException &&
        error.name === 'NotFoundError' &&
        referenceNode &&
        referenceNode.parentNode !== this
      ) {
        return this.appendChild(newNode);
      }
      throw error;
    }
  };

  (Node.prototype.removeChild as unknown as (this: Node, child: Node) => Node) =
    function removeChildGuard(child: Node) {
    try {
      return originalRemoveChild.call(this, child);
    } catch (error) {
      if (
        error instanceof DOMException &&
        error.name === 'NotFoundError' &&
        child.parentNode !== this
      ) {
        return child;
      }
      throw error;
    }
    };

  hasInstalledEdgeDomGuard = true;
  console.warn('[edge_dom_guard] installed');
}

export default class AppRootErrorBoundary extends Component<
  AppRootErrorBoundaryProps,
  AppRootErrorBoundaryState
> {
  state: AppRootErrorBoundaryState = {
    hasError: false,
    errorName: '',
    errorMessage: '',
    occurredAt: '',
    componentStack: '',
  };

  static getDerivedStateFromError(error: Error): AppRootErrorBoundaryState {
    return {
      hasError: true,
      errorName: error.name || 'Error',
      errorMessage: error.message || 'Unknown client error',
      occurredAt: new Date().toLocaleString('ja-JP'),
      componentStack: '',
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      componentStack: errorInfo.componentStack || '',
    });
    console.error('[app_root_error_boundary] uncaught client error', {
      name: error.name,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      href: typeof window !== 'undefined' ? window.location.href : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    });
  }

  componentDidMount() {
    installEdgeDomGuard();
  }

  private handleRetry = () => {
    this.setState({
      hasError: false,
      errorName: '',
      errorMessage: '',
      occurredAt: '',
      componentStack: '',
    });
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="w-full max-w-2xl rounded-xl border border-red-200 bg-white p-6 shadow-sm">
          <p className="text-base font-extrabold text-red-700">クライアントエラーが発生しました</p>
          <p className="mt-2 text-sm font-medium text-slate-600">
            画面描画中に例外が発生したため、安全のため処理を停止しました。
          </p>
          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
            <p>
              <span className="font-bold">種別:</span> {this.state.errorName}
            </p>
            <p className="mt-1 break-all">
              <span className="font-bold">内容:</span> {this.state.errorMessage}
            </p>
            <p className="mt-1">
              <span className="font-bold">発生時刻:</span> {this.state.occurredAt}
            </p>
            {this.state.componentStack ? (
              <div className="mt-2">
                <p className="font-bold">発生コンポーネント:</p>
                <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap rounded border border-slate-200 bg-white p-2 text-xs leading-relaxed text-slate-700">
                  {this.state.componentStack}
                </pre>
              </div>
            ) : null}
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={this.handleRetry}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-100"
            >
              再描画を試す
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="rounded-md bg-slate-800 px-4 py-2 text-sm font-bold text-white hover:bg-slate-700"
            >
              ページを再読み込み
            </button>
          </div>
        </div>
      </div>
    );
  }
}
